import { computed } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';

import { PaginationState } from '@core/store/models/pagination-state.model';

const initialPaginationState: PaginationState = {
  isVisible: false,
  currentPage: 1,
  itemsPerPage: 10,
  totalItems: 0,
  startingPage: 1,
};

export const PaginationStore = signalStore(
  { providedIn: 'root' },
  withState(initialPaginationState),
  withComputed(store => {
    const totalPages = () =>
      Math.ceil(store.totalItems() / store.itemsPerPage()) || 1;

    return {
      totalPages: computed(() => totalPages()),
      startIndex: computed(
        () => (store.currentPage() - 1) * store.itemsPerPage() + 1
      ),
      endIndex: computed(() =>
        Math.min(store.currentPage() * store.itemsPerPage(), store.totalItems())
      ),
      isFirstPage: computed(() => store.currentPage() === 1),
      isLastPage: computed(() => store.currentPage() >= totalPages()),
      hasNextPage: computed(() => store.currentPage() < totalPages()),
      hasPreviousPage: computed(() => store.currentPage() > 1),
      pageNumbers: computed((): (number | string)[] => {
        const currentPage = store.currentPage();
        const tp = totalPages();
        if (tp <= 7) {
          return Array.from({ length: tp }, (_, i) => i + 1);
        }
        const pages: (number | string)[] = [];
        pages.push(1);
        if (currentPage <= 3) {
          pages.push(2, 3, 4, '...', tp);
        } else if (currentPage >= tp - 2) {
          pages.push('...', tp - 3, tp - 2, tp - 1, tp);
        } else {
          pages.push(
            '...',
            currentPage - 1,
            currentPage,
            currentPage + 1,
            '...',
            tp
          );
        }
        return pages;
      }),
      paginationText: computed(() => {
        const totalItems = store.totalItems();
        if (totalItems === 0) {
          return 'Showing 0 items';
        }
        const start = (store.currentPage() - 1) * store.itemsPerPage() + 1;
        const end = Math.min(
          store.currentPage() * store.itemsPerPage(),
          totalItems
        );
        return `Showing ${start}-${end} of ${totalItems}`;
      }),
    };
  }),
  withMethods(store => ({
    showPagination(config: {
      totalItems: number;
      itemsPerPage?: number;
      startingPage?: number;
    }): void {
      const { totalItems, itemsPerPage, startingPage } = config;
      const ipp = itemsPerPage ?? store.itemsPerPage();
      const start = startingPage ?? 1;
      patchState(store, {
        isVisible: true,
        totalItems,
        itemsPerPage: ipp,
        currentPage: start,
        startingPage: start,
      });
    },

    hidePagination(): void {
      patchState(store, { ...initialPaginationState });
    },

    setPage(page: number): void {
      const tp = Math.ceil(store.totalItems() / store.itemsPerPage());
      const validPage = Math.max(1, Math.min(page, tp || 1));
      patchState(store, { currentPage: validPage });
    },

    nextPage(): void {
      const tp = Math.ceil(store.totalItems() / store.itemsPerPage());
      const next = Math.min(store.currentPage() + 1, tp || 1);
      patchState(store, { currentPage: next });
    },

    previousPage(): void {
      const prev = Math.max(store.currentPage() - 1, 1);
      patchState(store, { currentPage: prev });
    },

    firstPage(): void {
      patchState(store, { currentPage: 1 });
    },

    lastPage(): void {
      const tp = Math.ceil(store.totalItems() / store.itemsPerPage());
      patchState(store, { currentPage: tp || 1 });
    },

    setItemsPerPage(itemsPerPage: number): void {
      const tp = Math.ceil(store.totalItems() / itemsPerPage);
      const validPage = Math.min(store.currentPage(), tp || 1);
      patchState(store, {
        itemsPerPage,
        currentPage: validPage > 0 ? validPage : 1,
      });
    },

    updateTotalItems(totalItems: number): void {
      const tp = Math.ceil(totalItems / store.itemsPerPage());
      const validPage = Math.min(store.currentPage(), tp || 1);
      patchState(store, {
        totalItems,
        currentPage: validPage > 0 ? validPage : 1,
      });
    },
  }))
);
